import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getClientId, getSessionId } from '@/lib/analytics/clientId';
import { toPattern } from '@/lib/analytics/pathPatterns';

const THROTTLE_MS = 30_000;

export function usePageViewTracker() {
  const location = useLocation();
  const lastLogged = useRef<{ path: string; at: number } | null>(null);

  useEffect(() => {
    // Skip admin routes — they'd inflate stats
    if (location.pathname.startsWith('/admin')) return;

    const now = Date.now();
    const last = lastLogged.current;
    if (last && last.path === location.pathname && now - last.at < THROTTLE_MS) return;
    lastLogged.current = { path: location.pathname, at: now };

    const clientId = getClientId();
    const sessionId = getSessionId();
    const pattern = toPattern(location.pathname);
    const referrer = typeof document !== 'undefined' ? document.referrer || null : null;

    // Fire and forget
    (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        await supabase.from('site_page_views').insert({
          path: location.pathname,
          path_pattern: pattern,
          referrer,
          client_id: clientId || null,
          session_id: sessionId || null,
          user_id: userData?.user?.id ?? null,
        });
      } catch {
        // silent
      }
    })();
  }, [location.pathname]);
}
